/**
 * HitlQuestionPanel — dispatches a HITL question to the appropriate form.
 *
 * Shared between ora (WorkflowDetailDrawer) and the Outlook add-in
 * (ReviewCheckpointPanel). Both wrap this in a DetailSection so the
 * "Input Needed" heading and card chrome are consistent across surfaces.
 *
 * Dispatch order (first match wins):
 *   0. an auto-resume WAIT              → informational banner + optional
 *      (auto_resume / waiting_for_         "Cancel run". Not a decision: the
 *      service_principal / lease_wait)     run resumes itself.
 *   1. type === "file_upload_decision" → HitlFileUploadDecisionPanel
 *      (two named dropzones + Yes/Reject; carrier_portal_complete gate)
 *   2. type === "file_upload"          → HitlFileUploadPanel
 *      (single bucket; legacy attach-only gate)
 *   3. type === "review_checkpoint"    → HitlChoicePanel (approve/reject +
 *      OR choices/actions present         optional policy-term radio picker)
 *   4. schema present                  → HitlSubmitter (generic typed form)
 *                                        + evidence preview
 *   5. fields array                    → inline labeled field form
 *   6. fallback                        → simple Approve / Reject buttons
 *
 * Why file_upload* takes precedence over schema: the substrate emits BOTH
 * ``type: "file_upload_decision"`` AND a ``schema`` (the schema covers the
 * decision + optional reject-reason fields). Before this dispatch existed
 * the schema branch won and rendered a generic JSON-schema form with no
 * dropzones — instance 510555f9 (2026-05-29) hit exactly that: AM saw an
 * Approve button, clicked it without uploading anything, and the workflow
 * raced past the file-attach gate with no documents. The typed dispatch
 * must beat the schema dispatch so the proper upload UI shows.
 *
 * Why review_checkpoint beats schema: every warm-hold question built by
 * ``_build_review_checkpoint_question`` includes a ``schema`` field for
 * server-side validation. Before this fix, the schema branch won and
 * rendered HitlSubmitter — which never renders the policy-term radio picker
 * stored in ``policy_term_choices``. HitlChoicePanel is the right home for
 * review_checkpoint questions: it mirrors the schema's reject-requires-notes
 * constraint client-side and submits ``policy_term_match_index`` when an
 * override is selected.
 */
import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { formatFieldValue } from "../formatFieldValue";
import { renderInlineBold } from "../renderInlineBold";
import { HitlSubmitter } from "./HitlSubmitter";
import type { ConfidenceLevel } from "./JsonSchemaForm";
import { HitlChoicePanel } from "./HitlChoicePanel";
import { HitlFileUploadPanel } from "./HitlFileUploadPanel";
import { HitlFileUploadDecisionPanel } from "./HitlFileUploadDecisionPanel";
import { HitlEvidencePreview, type HitlStep } from "./HitlEvidencePreview";
import { leaseWaitMessage } from "../leaseWaitMessage";

export interface HitlQuestionPanelProps {
  /** Raw HITL question payload from the substrate. */
  hitlQuestion: Record<string, unknown>;
  /**
   * Called with the unwrapped response payload. The parent wires this to its
   * transport (Remix fetcher in ora, orchestratorApi.resumeInstance in plugin).
   */
  onSubmit: (payload: Record<string, unknown>) => void;
  isSubmitting: boolean;
  /** Submission error shown above the form. Owned by the parent's transport layer. */
  error?: string | null;
  /**
   * Href of the page that owns the work behind this gate, rendered as the
   * primary affordance above the decision pills. Only used by
   * ``type: "report_review"`` today (the quote-compare editor). Optional so the
   * Outlook add-in, which has no ora router and a different origin, simply
   * omits it and renders the gate unchanged.
   */
  reviewHref?: string;
  /**
   * Per-field extraction confidence for the values being reviewed, keyed by
   * top-level schema property (dotted ``"<arrayKey>.<subKey>"`` for moved
   * sub-fields — see ``_nest_field_confidence`` in the substrate).
   *
   * Forwarded to the schema form so the chip and its coloured left border
   * render on the DECISION surface, not just the launch form. Host-supplied:
   * both hosts already hold the analyze_email prefill result that carries it.
   */
  fieldConfidence?: Record<string, ConfidenceLevel>;
}

function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Is this the v2 SP-pool lease wait? */
export function isLeaseWaitQuestion(q: Record<string, unknown>): boolean {
  return q.kind === "lease_wait" || q.type === "lease_wait";
}

export { leaseWaitMessage } from "../leaseWaitMessage";

const INPUT_CLASS =
  "rounded-md border border-primary-gray-light-300 px-3 py-2 text-sm focus:outline-none focus:border-sunrise focus:ring-1 focus:ring-sunrise disabled:opacity-50";

export function HitlQuestionPanel({
  hitlQuestion,
  onSubmit,
  isSubmitting,
  error,
  reviewHref,
  fieldConfidence,
}: HitlQuestionPanelProps) {
  const steps = Array.isArray(hitlQuestion.steps_reviewed)
    ? (hitlQuestion.steps_reviewed as HitlStep[])
    : undefined;

  // The substrate's question builders always echo instance_id in the
  // question payload (see ``_carrier_portal_complete_question`` etc.);
  // we use it for the file-upload panels' response envelope. Empty-string
  // fallback lets the panels render even on legacy payloads — the
  // /resume API call routes by URL, not body.instance_id.
  const questionType =
    typeof hitlQuestion.type === "string" ? hitlQuestion.type : undefined;
  const instanceId =
    typeof hitlQuestion.instance_id === "string" ? hitlQuestion.instance_id : "";

  const hasSchema =
    hitlQuestion.schema != null && typeof hitlQuestion.schema === "object";

  // When the policy search returned >1 matching term the substrate injects
  // policy_term_choices into the question. HitlChoicePanel renders the radio
  // picker. We must route here BEFORE the hasSchema branch because
  // verify_policy_detail always carries a schema (notes/reject requirement)
  // and the schema branch dispatches to HitlSubmitter which has no picker.
  const rawTermChoices = hitlQuestion.policy_term_choices;
  const hasPolicyTermPicker =
    Array.isArray(rawTermChoices) &&
    (rawTermChoices as unknown[]).filter(
      (c) =>
        typeof c === "object" &&
        c !== null &&
        typeof (c as Record<string, unknown>).match_index === "number",
    ).length > 1;

  const choices = Array.isArray(hitlQuestion.choices)
    ? (hitlQuestion.choices as string[])
    : Array.isArray(hitlQuestion.actions)
      ? (hitlQuestion.actions as string[])
      : null;

  const fields = Array.isArray(hitlQuestion.fields)
    ? (hitlQuestion.fields as Array<{ key: string; label?: string; type?: string }>)
    : null;

  const question =
    typeof hitlQuestion.question === "string"
      ? hitlQuestion.question
      : typeof hitlQuestion.pending_action === "string"
        ? hitlQuestion.pending_action
        : "Respond to continue";

  // Auto-resume waits (e.g. waiting_for_service_principal) are NOT decision
  // gates — the run resumes itself once the awaited resource frees (the
  // SP-lease nudge cron / the durable self-poll), so they must render an
  // informational banner, never Approve/Reject (which the wait loop ignores).
  // `allow_cancel`, when set, offers the one honored escape hatch: a
  // {action:"cancel"} response that user_sp_wait_race aborts on.
  //
  // `lease_wait` is v2's SP-POOL wait and belongs here for the same reasons,
  // but arrives by a different route and so was missed: v1.2's
  // `waiting_for_service_principal` sets `auto_resume` and carries its own
  // prose, while `lease_wait` sets neither. It therefore fell through every
  // branch to the generic Approve/Reject fallback, and a run queued for a
  // session told the AM "NEEDS YOUR INPUT / Respond to continue" over two
  // buttons the gate does not declare (local instance f6bc24bf, 2026-09-04 --
  // it acquired a slot 1m45s later and carried on with nothing pressed).
  // Neither button is destructive -- `lease_wait_race` treats any non-cancel
  // response as a nudge to retry the acquire -- but the panel was asking for
  // input that does not exist and withholding the cancel the payload's own
  // contract says the UI "must show".
  const isLeaseWait = isLeaseWaitQuestion(hitlQuestion);
  const autoResumeWait =
    hitlQuestion.auto_resume === true ||
    hitlQuestion.kind === "waiting_for_service_principal" ||
    questionType === "waiting_for_service_principal" ||
    isLeaseWait;
  const allowCancel = hitlQuestion.allow_cancel === true;

  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  // A report_review gate pauses ON a document the user is meant to open and
  // edit; ending without reopening it is the wrong default. Render the editor
  // link as the primary action above the pills.
  const showReviewLink = questionType === "report_review" && !!reviewHref;

  return (
    <div className="flex flex-col gap-3">
      {showReviewLink && (
        <a
          href={reviewHref}
          className="btn-primary !border-blossom flex-1 text-sm rounded-lg border-[1px] border-solid px-[0.75rem] py-[0.5rem] font-bold text-center no-underline"
          data-testid="hitl-review-open-editor"
        >
          Open report editor
        </a>
      )}

      {error && (
        <p className="text-xs text-primary-error-700 bg-primary-error-50 rounded-xl px-3 py-2 border border-primary-error-200">
          {error}
        </p>
      )}

      {autoResumeWait ? (
        <div className="flex flex-col gap-3" data-testid="hitl-auto-resume-wait">
          <div className="flex items-start gap-2 rounded-xl border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2">
            <span className="mt-0.5 shrink-0 inline-flex text-sunrise">
              <CircularProgress size={16} color="inherit" />
            </span>
            <p className="text-sm text-primary-gray-light-800 whitespace-pre-wrap">
              {renderInlineBold(
                isLeaseWait ? leaseWaitMessage(hitlQuestion) : question,
              )}
            </p>
          </div>
          {allowCancel && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onSubmit({ action: "cancel" })}
              className="self-start h-9 px-3 flex justify-center items-center rounded-lg border border-primary-gray-light-300 text-sm font-semibold text-primary-gray-light-700 hover:bg-primary-gray-light-50 disabled:opacity-40"
              data-testid="hitl-auto-resume-cancel"
            >
              {isSubmitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                "Cancel run"
              )}
            </button>
          )}
        </div>
      ) : questionType === "file_upload_decision" ? (
        <HitlFileUploadDecisionPanel
          instanceId={instanceId}
          hitlQuestion={hitlQuestion}
          onSubmit={onSubmit}
          submittingOverride={isSubmitting}
        />
      ) : questionType === "file_upload" ? (
        <HitlFileUploadPanel
          instanceId={instanceId}
          hitlQuestion={hitlQuestion}
          onSubmit={onSubmit}
          submittingOverride={isSubmitting}
        />
      ) : questionType === "review_checkpoint" || hasPolicyTermPicker || choices ? (
        // review_checkpoint questions carry a schema (for server-side
        // validation) AND actions, but must route here — not HitlSubmitter —
        // so HitlChoicePanel can render the policy-term radio picker when
        // policy_term_choices is present. HitlSubmitter never reaches the
        // choices/radio branch. The schema's decision+notes constraint is
        // mirrored client-side in HitlChoicePanel (reject requires notes).
        <HitlChoicePanel
          hitlQuestion={hitlQuestion}
          fieldConfidence={fieldConfidence}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
        >
          <HitlEvidencePreview steps={steps} />
        </HitlChoicePanel>
      ) : hasSchema ? (
        <HitlSubmitter
          hitlQuestion={hitlQuestion}
          onSubmit={onSubmit}
          submittingOverride={isSubmitting}
          fieldConfidence={fieldConfidence}
        >
          <HitlEvidencePreview steps={steps} />
        </HitlSubmitter>
      ) : fields ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-primary-gray-light-800 whitespace-pre-wrap">
            {renderInlineBold(question)}
          </p>
          <div className="flex flex-col gap-2">
            {fields.map((f) => {
              const isMultiline = f.type === "multiline";
              const rawDefault = (f as Record<string, unknown>).value;
              const displayValue =
                fieldValues[f.key] !== undefined
                  ? fieldValues[f.key]
                  : rawDefault !== undefined
                    ? formatFieldValue(rawDefault)
                    : "";
              return (
                <div key={f.key} className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-primary-gray-light-700">
                    {f.label ?? titleCase(f.key)}
                  </label>
                  {isMultiline ? (
                    <textarea
                      rows={4}
                      value={displayValue}
                      onChange={(e) =>
                        setFieldValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                      }
                      disabled={isSubmitting}
                      className={`${INPUT_CLASS} resize-y whitespace-pre-wrap font-mono`}
                    />
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : "text"}
                      value={displayValue}
                      onChange={(e) =>
                        setFieldValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                      }
                      disabled={isSubmitting}
                      className={INPUT_CLASS}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => onSubmit(fieldValues)}
            className="h-10 px-4 flex justify-center items-center btn-primary rounded-lg disabled:opacity-40 text-sm font-semibold"
          >
            {isSubmitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              "Submit Response"
            )}
          </button>
        </div>
      ) : (
        // Generic Approve/Reject fallback (e.g. the oversight_level_review gate,
        // which carries no schema/choices/fields). Submits the SAME canonical
        // ``{choice}`` decision shape as HitlChoicePanel — NOT the legacy
        // ``{approved: bool}`` envelope. That divergent shape cancelled an
        // APPROVED destructive step: the substrate's canonical_hitl_action reads
        // decision/action/choice, so ``{approved: true}`` normalized to None and
        // the destructive fail-closed gate read it as "operator rejected"
        // (instance ed1b9801). One decision contract across every panel.
        <div className="flex flex-col gap-3">
          <p className="text-sm text-primary-gray-light-800 whitespace-pre-wrap">
            {renderInlineBold(question)}
          </p>
          {/* oversight_level_review now ships steps_reviewed (graphs/oversight.py);
              renders null when the payload carries no screenshot. */}
          <HitlEvidencePreview steps={steps} />
          <div className="flex gap-3">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onSubmit({ choice: "Approve" })}
              className="flex-1 h-10 px-4 flex justify-center items-center btn-primary rounded-lg disabled:opacity-40 text-sm font-semibold"
            >
              {isSubmitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                "Approve"
              )}
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onSubmit({ choice: "Reject" })}
              className="flex-1 h-10 px-4 flex justify-center items-center rounded-lg border border-primary-gray-light-300 text-sm font-semibold text-primary-gray-light-700 hover:bg-primary-gray-light-50 disabled:opacity-40"
            >
              {isSubmitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                "Reject"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HitlQuestionPanel;
